import getZipCodeProtection from "@faire/web-api/api/retailer/retailerToken/zip-code-protected-brands/get";
import { trackZipCodeProtectionBrandBlocked } from "@faire/web-api/events/zipCodeProtection/actionUnknown/brandBlocked";
import { IGetZipCodeProtectionResponse } from "@faire/web-api/indigofair/data/IGetZipCodeProtectionResponse";
import { cachingApi } from "@faire/web/common/cachingApi";
import { singletonGetter } from "@faire/web/common/singletons/getter";
import { createStoreHook } from "@faire/web/ui/hooks/useStore";
import { computed, observable } from "mobx";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";

import { RetailerStore } from "./RetailerStore";

/**
 * Faire protection can be enabled per brand/maker and is awarded to one
 * retailer per zipcode / postal code for a 90 day period.
 *
 * This store allows one to query whether or not the current logged in
 * retailer is able to place an order from a particular maker/brand
 *
 * NOTE: this logic does NOT take into account other reasons a retailer
 * may not be able to order from a maker (like residing in a forbidden state
 * for that maker)
 * @deprecated prefer to use `useFaireProtection` hook
 */
export class FaireProtectionStore {
  static get = singletonGetter(FaireProtectionStore);

  // Not private for testing purposes
  // unused-class-members-ignore-next
  getZipCodeProtectionMemoized = cachingApi(getZipCodeProtection, {
    // Cache for 3 minutes, and refetch if accessed after 2 minutes.
    // This is to avoid repeated fetches on pages like cart,
    // which need the data for each card in the cart.
    cacheExpiryTime: 1000 * 60 * 3,
    refetchBeforeExpiry: true,
  });

  @observable
  private fetchResponse?: IPromiseBasedObservable<IGetZipCodeProtectionResponse>;

  fetchData = async () => {
    const { retailerToken, isValidRetailer } = RetailerStore.get();
    if (!isValidRetailer || !retailerToken) {
      return;
    }
    await (this.fetchResponse = fromPromise(
      this.getZipCodeProtectionMemoized(retailerToken),
      this.fetchResponse
    ));
  };

  @computed
  private get faireProtectionData(): IGetZipCodeProtectionResponse | undefined {
    return this.fetchResponse?.case({ fulfilled: (r) => r });
  }

  @computed
  get isLoading(): boolean {
    return (
      RetailerStore.get().isValidRetailer &&
      (!this.fetchResponse || this.fetchResponse.state === "pending")
    );
  }

  @computed
  get forbiddenBrandTokens(): string[] {
    return this.faireProtectionData?.brand_tokens ?? [];
  }

  @computed
  get zipCode(): string | undefined {
    return this.faireProtectionData?.zip_code;
  }

  isBrandTokenForbidden = (
    brandToken: string,
    options?: { trackReason?: true }
  ): boolean => {
    if (this.fetchResponse?.state !== "fulfilled") {
      return false;
    }
    const isBlocked = this.forbiddenBrandTokens.includes(brandToken);
    if (isBlocked && options?.trackReason) {
      const blockedReasons =
        this.faireProtectionData?.blocked_reasons[brandToken]?.reason_codes ??
        [];
      blockedReasons.forEach((reason) =>
        trackZipCodeProtectionBrandBlocked(brandToken, reason.toString())
      );
    }
    return isBlocked;
  };

  expirationTime = (brandToken: string): number | undefined => {
    return this.faireProtectionData?.protected_for_retailer?.[brandToken];
  };
}

export const useFaireProtectionStore = createStoreHook(FaireProtectionStore);
